import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFliptBoolean } from "@flipt-io/flipt-client-react";
import {
  alpha,
  Chip,
  Divider,
  IconButton,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  styled,
} from "@mui/material";
import yn from "yn";
import UserAvatar from "~/components/UserAvatar";
import useUser from "~/contexts/user";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { ROUTE_ATLAS_VIEW } from "~/router/constants";

const { VITE_SUPPORT_EMAIL, VITE_HELP_CENTER_ENABLED } = import.meta.env;

const HELP_CENTER_URL = "https://support.manifold.ai/hc/en-us";
const HELP_CENTER_REQUEST_URL = `${HELP_CENTER_URL}/requests/new`;

const StyledMenuItem = styled(MenuItem)({
  padding: 0,
  a: {
    width: "100%",
    padding: "8px 16px",
  },
});

const AccountMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const user = useUser();
  const [, updatePreferences] = useUserPreferences();
  const showAtlasRedesign = useFliptBoolean("atlas-redesign", false, user?.email);
  const isAtlasView = pathname.startsWith(ROUTE_ATLAS_VIEW);

  const handleClose = () => setAnchorEl(null);

  const handleOptIntoAtlasRedesign = () => {
    updatePreferences({ optedIntoAtlasRedesign: true });
    handleClose();
    navigate(`${ROUTE_ATLAS_VIEW}${pathname}${search}`);
  };

  const handleOptOutOfAtlasRedesign = () => {
    updatePreferences({ optedIntoAtlasRedesign: false });
    handleClose();
    navigate(pathname.replace(ROUTE_ATLAS_VIEW, "") || "/");
  };
  const helpCenterEnabled = yn(VITE_HELP_CENTER_ENABLED, { default: true });
  const newTabLinkProps = { target: "_blank", rel: "noopener noreferrer" };

  return (
    <>
      <IconButton
        id="account-button"
        aria-controls={anchorEl ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          p: 0.5,
          "&[aria-expanded='true'], :hover": { bgcolor: "rgba(255, 255, 255, 0.16)" },
        }}
      >
        <UserAvatar size={28} />
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "account-button",
        }}
        PaperProps={{ sx: { borderRadius: 2 }, elevation: 2 }}
        sx={{ marginTop: 0.5, marginLeft: 1 }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {showAtlasRedesign && (
          <>
            {isAtlasView ? (
              <StyledMenuItem onClick={handleOptOutOfAtlasRedesign} sx={{ px: 2, py: 1 }}>
                <ListItemText>Switch to classic look</ListItemText>
              </StyledMenuItem>
            ) : (
              <StyledMenuItem onClick={handleOptIntoAtlasRedesign} sx={{ px: 2, py: 1 }}>
                <ListItemText>Try the new look</ListItemText>
                <Chip
                  label="Beta"
                  size="small"
                  sx={(theme) => ({
                    ml: 1,
                    height: 18,
                    fontSize: 10,
                    bgcolor: "neutral.150",
                    color: "inherit",
                    transition: "none",
                    ".MuiMenuItem-root:hover &": {
                      bgcolor: alpha(theme.palette.neutral.white, 0.25),
                    },
                  })}
                />
              </StyledMenuItem>
            )}
            <Divider sx={{ mx: 2 }} />
          </>
        )}
        {helpCenterEnabled && (
          <StyledMenuItem>
            <Link
              underline="none"
              onClick={handleClose}
              href={HELP_CENTER_URL}
              color="inherit"
              {...newTabLinkProps}
            >
              Help Center
            </Link>
          </StyledMenuItem>
        )}
        <StyledMenuItem>
          <Link
            underline="none"
            onClick={handleClose}
            href={`${VITE_SUPPORT_EMAIL ? `mailto:${VITE_SUPPORT_EMAIL}` : HELP_CENTER_REQUEST_URL}`}
            color="inherit"
            {...(!VITE_SUPPORT_EMAIL ? newTabLinkProps : {})}
          >
            Contact Support
          </Link>
        </StyledMenuItem>
        <Divider sx={{ mx: 2 }} />
        <StyledMenuItem>
          <Link underline="none" onClick={handleClose} href="/logout" color="inherit">
            Sign Out
          </Link>
        </StyledMenuItem>
      </Menu>
    </>
  );
};

export default AccountMenu;
